import { Fragment } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/utils';

type AlertDialogVariant = 'danger' | 'warning' | 'info' | 'success';

type AlertDialogProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    variant?: AlertDialogVariant;
    isLoading?: boolean;
};

const variantConfig = {
    danger: {
        icon: AlertTriangle,
        iconBg: 'bg-red-100 dark:bg-red-900/20',
        iconColor: 'text-red-600 dark:text-red-400',
        confirmVariant: 'destructive' as const,
    },
    warning: {
        icon: AlertTriangle,
        iconBg: 'bg-yellow-100 dark:bg-yellow-900/20',
        iconColor: 'text-yellow-600 dark:text-yellow-400',
        confirmVariant: 'primary' as const,
    },
    info: {
        icon: Info,
        iconBg: 'bg-blue-100 dark:bg-blue-900/20',
        iconColor: 'text-blue-600 dark:text-blue-400',
        confirmVariant: 'primary' as const,
    },
    success: {
        icon: CheckCircle,
        iconBg: 'bg-green-100 dark:bg-green-900/20',
        iconColor: 'text-green-600 dark:text-green-400',
        confirmVariant: 'primary' as const,
    },
};

export function AlertDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    isLoading = false,
}: AlertDialogProps) {
    const config = variantConfig[variant];
    const Icon = config.icon;

    const handleConfirm = () => {
        onConfirm();
        if (!isLoading) {
            onClose();
        }
    };

    if (typeof document === 'undefined') return null;

    return createPortal(
        <Transition show={open} as={Fragment}>
            <Dialog onClose={onClose} className="relative z-[10100]">
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-150"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40" />
                </Transition.Child>

                {/* Dialog Panel */}
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-150"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-100"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900 dark:border dark:border-gray-700">
                                {/* Icon */}
                                <div className={cn('mx-auto flex h-12 w-12 items-center justify-center rounded-full', config.iconBg)}>
                                    <Icon className={cn('h-6 w-6', config.iconColor)} />
                                </div>

                                {/* Content */}
                                <div className="mt-4 text-center">
                                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {title}
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {description}
                                        </p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="mt-6 flex gap-3">
                                    <Button
                                        variant="outline"
                                        className="flex-1"
                                        onClick={onClose}
                                        disabled={isLoading}
                                    >
                                        {cancelText}
                                    </Button>
                                    <Button
                                        variant={config.confirmVariant}
                                        className="flex-1"
                                        onClick={handleConfirm}
                                        disabled={isLoading}
                                        isLoading={isLoading}
                                    >
                                        {confirmText}
                                    </Button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>,
        document.body
    );
}
